import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
import type { LoanMessage } from '@ld.consumer-portal/models';
import {
  DynamicMessageBannerComponent,
  DynamicMessageBannerType,
} from '../dynamic-message-banner/dynamic-message-banner.component';

@Component({
  selector: 'ld-customer-message-container',
  imports: [DynamicMessageBannerComponent],
  template: `
    @for (message of visibleMessages(); track trackByMessage($index, message)) {
      <ld-dynamic-message-banner
        [id]="'customer-message-' + $index"
        [bannerId]="$index.toString()"
        [type]="getMessageType(message)"
        [title]="getMessageTitle(message)"
        [message]="getMessageBody(message)"
        [expandable]="true"
        [dismissible]="false"
      />
    }
  `,
  styles: `
    :host {
      display: block;
    }
    :host:empty {
      display: none;
    }
    ld-dynamic-message-banner {
      display: block;
      margin-bottom: 1rem;
    }
    ld-dynamic-message-banner:last-child {
      margin-bottom: 0;
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CustomerMessageContainerComponent {
  readonly messages = input<LoanMessage[]>([]);

  protected readonly visibleMessages = computed(() =>
    this.messages().filter((m) => this.isMessageVisible(m.startTime, m.endTime)),
  );

  private isMessageVisible(startTime: string, endTime: string): boolean {
    if (!startTime || !endTime) {
      return true;
    }

    const start = new Date(startTime);
    const end = new Date(endTime);
    const now = new Date();

    return start < now && now < end;
  }

  protected getMessageType(message: LoanMessage): DynamicMessageBannerType {
    const type = message.messageContent?.messageType?.toLowerCase();
    switch (type) {
      case 'alert1':
      case 'warning':
        return 'warning';
      case 'alert2':
      case 'error':
        return 'error';
      case 'success':
        return 'success';
      case 'info':
      case 'info2':
      default:
        return 'info';
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  protected getMessageTitle(_message: LoanMessage): string {
    return '';
  }

  protected getMessageBody(message: LoanMessage): string {
    return message.messageContent?.body || '';
  }

  protected trackByMessage(index: number, message: LoanMessage): string {
    return `${index}-${message.loanNumber}-${message.messageContent?.body?.slice(0, 20) || ''}`;
  }
}
